import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'vendor/heroui';
import { Modal } from 'components/HeroSemantic';
import { display } from 'state/modules/dialog';
import passkeyButtonClassName, { passkeyPrimaryButtonClassName } from 'components/Auth/passkeyButtonClassName';

const downloadCodes = codes => {
  const blob = new Blob([codes.join('\n')], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'recovery-codes.txt';
  link.click();
  URL.revokeObjectURL(url);
};

// Shown exactly once, right after owner-api issues a fresh batch (on the
// account's first passkey, or a manual regeneration) - shared so this can't
// silently be skipped from any entry point that creates a first passkey
// (e.g. the login nudge), since these codes are never retrievable again.
const RecoveryCodesModal = ({ codes, onDone }) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const handleCopy = () => {
    navigator.clipboard.writeText((codes ?? []).join('\n')).catch(() => {
      dispatch(display(
        formatMessage({ id: 'notifications.title.error' }),
        formatMessage({ id: 'page.passkeys.recovery.codes.copyFailed', defaultMessage: 'Could not copy codes to the clipboard - please use Download instead.' }),
        'error'
      ));
    });
  };

  return (
    // No onClose - dismissing via the backdrop would silently discard a
    // one-time-only view of the codes. Only the explicit "I've saved these
    // codes" button below may invoke onDone.
    <Modal open={Boolean(codes)}>
      <Modal.Header>
        <FormattedMessage id="page.passkeys.recovery.codes.title" defaultMessage="Your recovery codes" />
      </Modal.Header>
      <Modal.Content>
        <p className="mb-4 text-sm text-[var(--owner-text)]">
          <FormattedMessage
            id="page.passkeys.recovery.codes.body"
            defaultMessage="Save these codes somewhere safe. Each one can be used once to sign in if you lose access to your passkey, and they won't be shown again."
          />
        </p>
        <ul className="grid grid-cols-2 gap-2 rounded-xl border border-[var(--owner-border)] bg-[var(--owner-surface-muted)] p-4 font-mono text-sm text-[var(--owner-text)]">
          {(codes ?? []).map(code => <li key={code}>{code}</li>)}
        </ul>
      </Modal.Content>
      <Modal.Actions>
        <Button type="button" className={passkeyButtonClassName} onPress={handleCopy}>
          <FormattedMessage id="page.passkeys.recovery.codes.copy" defaultMessage="Copy all" />
        </Button>
        <Button type="button" className={passkeyButtonClassName} onPress={() => downloadCodes(codes ?? [])}>
          <FormattedMessage id="page.passkeys.recovery.codes.download" defaultMessage="Download" />
        </Button>
        <Button type="button" className={passkeyPrimaryButtonClassName} onPress={onDone}>
          <FormattedMessage id="page.passkeys.recovery.codes.done" defaultMessage="I've saved these codes" />
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

RecoveryCodesModal.propTypes = {
  codes: PropTypes.arrayOf(PropTypes.string),
  onDone: PropTypes.func.isRequired,
};

RecoveryCodesModal.defaultProps = {
  codes: null,
};

export default RecoveryCodesModal;
